import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Film, PlayCircle, Loader2, CheckCircle2, XCircle } from "lucide-react";
import api from "@/lib/api";

const STATUS_META = {
  processing: { icon: Loader2, cls: "text-amber-400 animate-spin", label: "AI notes generating..." },
  ready: { icon: CheckCircle2, cls: "text-emerald-400", label: "AI notes ready" },
  failed: { icon: XCircle, cls: "text-rose-400", label: "AI notes failed" },
};

export default function RecordingsPage() {
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/meetings/recordings/mine").then((res) => setRecordings(res.data));

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!recordings.some((r) => r.transcript_status === "processing")) return;
    const t = setTimeout(load, 6000);
    return () => clearTimeout(t);
  }, [recordings]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="recordings-loading">
        <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-8" data-testid="recordings-page">
      <div className="flex items-center gap-2 mb-1">
        <Film className="w-5 h-5 text-cyan-400" />
        <h1 className="font-heading text-xl font-semibold text-white">Recordings</h1>
      </div>
      <p className="text-sm text-slate-400 mb-6">Meetings you've hosted and recorded — each one gets real AI Voice Notes (Whisper transcript + GPT summary), and can be imported straight into a Creator Studio course.</p>

      {recordings.length === 0 ? (
        <div className="glass-panel rounded-2xl p-10 text-center" data-testid="recordings-empty">
          <PlayCircle className="w-9 h-9 text-slate-500 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">No recordings yet. Start a meeting as host and hit the record button to capture your first session.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {recordings.map((r) => {
            const meta = STATUS_META[r.transcript_status] || STATUS_META.processing;
            const Icon = meta.icon;
            return (
              <Link key={r.id} to={`/meetings/recordings/${r.id}`} data-testid={`recording-row-${r.id}`}
                className="glass-panel rounded-xl p-4 flex items-center justify-between gap-4 hover:bg-white/[0.04] transition-colors">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-white truncate flex items-center gap-2">
                    {r.title}
                    {r.source === "cloud_recording" && (
                      <span data-testid={`recording-cloud-badge-${r.id}`} className="text-[10px] font-bold uppercase text-cyan-300 bg-cyan-400/10 px-1.5 py-0.5 rounded-full shrink-0">Cloud Recording</span>
                    )}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">{r.meeting_title} · {new Date(r.created_at).toLocaleDateString()}</p>
                </div>
                <span className={`text-xs flex items-center gap-1.5 shrink-0 ${meta.cls.replace("animate-spin", "")}`}>
                  <Icon className={`w-3.5 h-3.5 ${meta.cls.includes("animate-spin") ? "animate-spin" : ""}`} /> {meta.label}
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
